'use client';
import {useEffect, useState} from 'react';
import {ThemeProvider} from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import darkTheme from '@/src/themes/dark-theme';
import lightTheme from '@/src/themes/light-theme';
import LayoutProps from '@/src/_types/layout-props';
import createEmotionCache from "@mui/material-nextjs/v13-pagesRouter/createCache";
import {useServerInsertedHTML} from "next/navigation";
import {CacheProvider} from "@emotion/react";
import ThemeContext from "@/src/context/theme-context";


export default function ThemeRegistry({children}: LayoutProps) {
    const [theme, setTheme] = useState<'light' | 'dark'>('light');

    const [{cache, flush}] = useState(() => {
        const cache = createEmotionCache();
        cache.compat = true;
        const prevInsert = cache.insert;
        let inserted: any[] = [];
        cache.insert = (...args) => {
            const serialized = args[1];
            if (cache.inserted[serialized.name] === undefined) {
                inserted.push(serialized.name);
            }
            return prevInsert(...args);
        };
        const flush = () => {
            const prevInserted = inserted;
            inserted = [];
            return prevInserted;
        };
        return {cache, flush};
    });

    useServerInsertedHTML(() => {
        const names = flush();
        if (names.length === 0) {
            return null;
        }
        let styles = '';
        for (const name of names) {
            styles += cache.inserted[name];
        }
        return (
            <style
                key={cache.key}
                data-emotion={`${cache.key} ${names.join(' ')}`}
                dangerouslySetInnerHTML={{
                    __html: styles,
                }}
            />
        );
    });

    useEffect(() => {
        const savedTheme = localStorage?.getItem('theme');
        if (savedTheme) {
            setTheme(savedTheme as 'light' | 'dark');
        }
    }, []);

    const toggleTheme = () => {
        const newTheme = theme === 'light' ? 'dark' : 'light';
        setTheme(newTheme);
        localStorage?.setItem('theme', newTheme);
    };

    return (
        <CacheProvider value={cache}>
            <ThemeContext.Provider value={{theme, toggleTheme}}>
                <ThemeProvider theme={theme === 'light' ? lightTheme : darkTheme}>
                    <CssBaseline/>
                    {children}
                </ThemeProvider>
            </ThemeContext.Provider>
        </CacheProvider>
    );
}
